import React, { useEffect, useState } from 'react';
import { AlertTriangle, Database, ExternalLink } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { datasetApi } from '../../../api/datasetApi';

const formatCompactNumber = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return new Intl.NumberFormat(undefined, {
    notation: n >= 10000 ? 'compact' : 'standard',
    maximumFractionDigits: 1
  }).format(n);
};

const getRowCount = (metadata, dataset) =>
  metadata?.stats?.num_rows ??
  metadata?.statistics?.num_rows ??
  dataset?.stats?.num_rows ??
  dataset?.statistics?.num_rows ??
  null;

const getColumnCount = (metadata, dataset) => {
  if (typeof metadata?.column_count === 'number') return metadata.column_count;
  if (typeof dataset?.column_count === 'number') return dataset.column_count;
  if (Array.isArray(metadata?.schema)) return metadata.schema.length;
  return null;
};

const FIELD_VALUE_CLASS = 'text-sm font-medium text-gray-700 dark:text-gray-300 leading-relaxed';

const StatPill = ({ children }) => (
  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-[10px] tabular-nums text-gray-600 dark:bg-gray-800/80 dark:text-gray-400">
    {children}
  </span>
);

const DatasetPreview = ({ dataset, onOpenDetails }) => {
  const [metadata, setMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!dataset) {
      setMetadata(null);
      return;
    }

    const fetchMetadata = async () => {
      setIsLoading(true);
      try {
        const data = await datasetApi.getDatasetMetadata(dataset.id);
        setMetadata(data);
      } catch (err) {
        console.error('Failed to fetch dataset metadata:', err);
        setMetadata(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetadata();
  }, [dataset]);

  if (!dataset) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center p-8">
        <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg mb-3">
          <Database size={24} className="text-gray-300 dark:text-gray-700" />
        </div>
        <h4 className="text-xs font-medium text-gray-400 dark:text-gray-600 uppercase tracking-wider mb-1">
          Select a dataset
        </h4>
        <p className="text-[10px] text-gray-400 dark:text-gray-600">
          Pick a source from the sidebar to preview
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-4 h-4 border-2 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin" />
          <span className="text-[10px] font-medium text-gray-400 uppercase tracking-wider">Loading...</span>
        </div>
      </div>
    );
  }

  const importFailed = metadata?.status === 'failed' || dataset.status === 'failed';
  const description =
    metadata?.summary ??
    dataset.summary ??
    'No description provided for this table yet.';
  const recordCount = getRowCount(metadata, dataset);
  const columnCount = getColumnCount(metadata, dataset);
  const formattedRecords = recordCount == null ? '—' : formatCompactNumber(recordCount);
  const formattedColumns = columnCount == null ? '—' : formatCompactNumber(columnCount);
  const displayName =
    dataset.type === 'bigquery' && dataset.name.includes('.')
      ? dataset.name.split('.').pop()
      : dataset.name;

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-950 transition-colors">
      {importFailed && (
        <div
          className="mx-4 mt-3 flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 dark:border-red-900/50 dark:bg-red-950/40"
          role="alert"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600 dark:text-red-400" aria-hidden />
          <p className="min-w-0 text-[11px] font-medium leading-snug text-red-800 dark:text-red-200">
            {(metadata?.summary && String(metadata.summary).trim()) ||
              (dataset.summary && String(dataset.summary).trim()) ||
              'No error message was recorded.'}
          </p>
        </div>
      )}

      <div className="flex flex-1 min-h-0 flex-col p-4">
        <p className={cn(FIELD_VALUE_CLASS, 'min-h-0 flex-1 overflow-y-auto custom-scrollbar')}>
          {importFailed ? 'Import failed for this dataset.' : description}
        </p>

        <div className="flex shrink-0 flex-wrap items-center gap-2 pt-3">
          <span
            className="min-w-0 max-w-[min(100%,14rem)] truncate text-[11px] font-semibold text-gray-900 dark:text-white"
            title={dataset.name}
          >
            {displayName}
          </span>
          <StatPill>{formattedRecords} rows</StatPill>
          <StatPill>{formattedColumns} columns</StatPill>
          <button
            type="button"
            onClick={onOpenDetails}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1',
              'text-[11px] font-semibold text-gray-500 transition-all',
              'hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700',
              'dark:border-gray-700 dark:text-gray-400 dark:hover:border-emerald-700 dark:hover:bg-emerald-900/20 dark:hover:text-emerald-300'
            )}
          >
            Details
            <ExternalLink size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatasetPreview;
